import type { ApiError } from '../errors';
import { accountsRefreshTokenCreate, ErrorCode401Enum } from '../generated';
import type { Client } from '../generated/client';
import type { AccountsRefreshTokenCreateResponse } from '../generated/types.gen';
import type { Singleton } from './common';

type Mutex = Promise<AccountsRefreshTokenCreateResponse | undefined>;

export interface RefreshOptions {
  token: Singleton<string>;
  client: Client;
  on_refresh_failed?: (error?: ApiError) => void;
}

let mutex: Mutex | null = null;

const _refresh = async (options: RefreshOptions): Mutex => {
  const handle_error = (error: ApiError) => {
    options.on_refresh_failed?.(error);
    options.token.clear();
  };

  const token = options.token.get();
  if (!token) {
    return undefined;
  }

  const response = await accountsRefreshTokenCreate({
    ...options,
    body: { token },
  });

  if (response.error) {
    handle_error(response.error);
    return undefined;
  }

  if (response.data?.token) {
    options.token.set(response.data.token);
    return response.data;
  }

  handle_error({
    type: ErrorCode401Enum.AUTHENTICATION_FAILED,
    errors: [{ code: 'no_token', detail: 'Refresh call succeeded but returned no token.', attr: null }],
  });

  return undefined;
};

export const refresh = (options: RefreshOptions): Mutex => {
  if (!mutex) {
    mutex = _refresh(options).finally(() => {
      mutex = null;
    });
  }

  return mutex;
};
